import React, { useMemo } from 'react';
import { analyzeSpamLocal, mergeSpamResults, spamLevelMeta } from './liveSpamCheck';
import './LiveSpamBadge.css';

/**
 * Small spam-risk indicator that sits next to the Send button. Shows a warning
 * icon only when the email carries elevated spam signals; hovering reveals the
 * exact trigger words and reasons.
 *
 * Props:
 *  - body, subject: current email content (used for the instant rule-based check)
 *  - mode: 'rules' | 'ai'
 *  - aiResult: normalized AI analysis ({level,triggers,notes,...}) when available
 *  - aiPending: true while the AI check is running (mode === 'ai')
 *  - isFollowUp: true when composing a follow-up (reply in an existing thread),
 *    where a "Re:/Fwd:" prefix is legitimate and must not be flagged as spam
 */
const FAKE_REPLY_NOTE_RE = /re:\s*\/\s*fwd:|fake\s+"?re:|"re:\/fwd:"/i;

const LiveSpamBadge = ({ body, subject, mode = 'rules', aiResult = null, aiPending = false, isFollowUp = false }) => {
  const localResult = useMemo(() => analyzeSpamLocal(body, subject, { isFollowUp }), [body, subject, isFollowUp]);

  // No loading spinner — we always show a definitive state. While an AI refresh
  // is in flight we simply show the instant local result (it's never worse than
  // showing nothing), and swap to the merged verdict once it lands.
  //
  // The AI verdict is MERGED with the rule-based one (never less strict) so the
  // badge can't turn green while the hardcoded checker still sees spam wording.
  const usingAi = mode === 'ai' && !!aiResult && !aiPending;
  const merged = usingAi ? mergeSpamResults(localResult, aiResult) : localResult;

  // Belt-and-suspenders: on a follow-up, drop any lingering "Re:/Fwd:" spam note
  // the AI may still emit — the prefix is legitimate inside an existing thread.
  const result = useMemo(() => {
    if (!isFollowUp) return merged;
    const notes = (merged.notes || []).filter((n) => !FAKE_REPLY_NOTE_RE.test(n.text || ''));
    if (notes.length === (merged.notes || []).length) return merged;
    return { ...merged, notes };
  }, [merged, isFollowUp]);

  const hasContent = (body && body.trim()) || (subject && subject.trim());
  if (!hasContent) return null;

  const meta = spamLevelMeta[result.level];
  const iconClass = result.level === 'danger'
    ? 'fa-solid fa-triangle-exclamation'
    : result.level === 'warn'
      ? 'fa-solid fa-circle-exclamation'
      : 'fa-solid fa-circle-check';
  const engineLabel = usingAi ? 'SalesGod Spam Analyzer' : 'Quick spam check';

  return (
    <div className="lsb-wrap">
      <button
        type="button"
        className={`lsb-badge lsb-badge--${result.level}`}
        aria-label={meta.label}
        onClick={(e) => e.preventDefault()}
      >
        <i className={iconClass} />
      </button>

      <div className="lsb-pop" role="tooltip">
        <div className={`lsb-pop-head lsb-pop-head--${result.level}`}>
          <i className={iconClass} />
          <span>{meta.label}</span>
          <span className="lsb-pop-score">{result.score}/10</span>
        </div>

        <div className="lsb-pop-body">
          {result.triggers.length > 0 && (
            <div className="lsb-pop-section">
              <div className="lsb-pop-label">Trigger words</div>
              <div className="lsb-chips">
                {result.triggers.slice(0, 10).map((t, i) => (
                  <span key={i} className="lsb-chip">
                    {t.word}{t.count > 1 ? ` ×${t.count}` : ''}
                  </span>
                ))}
              </div>
            </div>
          )}

          {result.notes.length > 0 && (
            <div className="lsb-pop-section">
              <div className="lsb-pop-label">Why this looks risky</div>
              <ul className="lsb-notes">
                {result.notes.slice(0, 5).map((n, i) => (
                  <li key={i}>{n.text}</li>
                ))}
              </ul>
            </div>
          )}

          {result.triggers.length === 0 && result.notes.length === 0 && (
            <div className="lsb-pop-section lsb-pop-generic">
              {result.level === 'ok'
                ? 'No spammy wording or risky patterns detected — this reads like a genuine personal email and should reach the inbox.'
                : 'This email reads promotional to strict filters (e.g. Microsoft 365). Soften the sales wording to improve inbox placement.'}
            </div>
          )}
        </div>

        <div className="lsb-pop-foot">
          <i className={usingAi ? 'fa-solid fa-wand-magic-sparkles' : 'fa-solid fa-gauge-high'} />
          {engineLabel}
          {mode === 'ai' && aiPending && <span className="lsb-pop-refreshing"> · refreshing…</span>}
        </div>
      </div>
    </div>
  );
};

export default LiveSpamBadge;
